import { useFormikContext } from 'formik';

import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';

import { Checkbox } from '@@/form-components/Checkbox';
import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { Input } from '@@/form-components/Input';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';

import {
  AutomaticNamespaceProvisioningFormValues,
  generateNamespace,
  NAMESPACE_ROLE_IDS,
  USER_ID_PLACEHOLDER,
  USER_SCOPE_OPTIONS,
} from './types';

const EXAMPLE_USER_ID = 42;

export function AutomaticNamespaceProvisioningFormFields() {
  const { values, errors, setFieldValue } =
    useFormikContext<AutomaticNamespaceProvisioningFormValues>();

  const roleOptionsQuery = useRbacRoles({
    select: (roles) =>
      roles
        .filter((role) => NAMESPACE_ROLE_IDS.includes(role.Id))
        .map((role) => ({ value: role.Id, label: role.Name })),
  });

  const examplePattern = values.namespacePattern.includes(USER_ID_PLACEHOLDER)
    ? generateNamespace(values.namespacePattern, EXAMPLE_USER_ID)
    : '';

  return (
    <>
      <FormSection title="About automatic namespace provisioning">
        <p className="text-muted text-sm">
          Each user in scope gets a personal namespace on every Kubernetes
          environment in the selected groups, with the chosen role granted
          inside it.
        </p>
      </FormSection>

      <FormSection title="User scope">
        <FormControl
          label="Authentication methods"
          inputId="anp-user-scope-internal"
          errors={errors.userScopes}
          tooltip="Provision namespaces only for users whose last login used one of these authentication methods."
          required
        >
          <div className="flex flex-wrap gap-4">
            {USER_SCOPE_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                id={`anp-user-scope-${option.label.toLowerCase()}`}
                data-cy={`anp-user-scope-${option.label.toLowerCase()}`}
                label={option.label}
                bold={false}
                checked={values.userScopes.includes(option.value)}
                onChange={(e) =>
                  toggleUserScope(option.value, e.target.checked)
                }
              />
            ))}
          </div>
        </FormControl>
      </FormSection>

      <FormSection title="Role">
        <FormControl
          label="Role"
          inputId="anp-namespace-role"
          errors={errors.namespaceRoleId}
          tooltip="Role granted to each user inside their own namespace."
          required
        >
          <PortainerSelect<number | undefined>
            inputId="anp-namespace-role"
            value={values.namespaceRoleId}
            onChange={(value) =>
              setFieldValue('namespaceRoleId', value ?? undefined)
            }
            options={roleOptionsQuery.data ?? []}
            isLoading={roleOptionsQuery.isLoading}
            placeholder="Select a role"
            bindToBody
            data-cy="anp-namespace-role-select"
          />
        </FormControl>
      </FormSection>

      <FormSection title="Namespace">
        <FormControl
          label="Namespace pattern"
          inputId="anp-namespace-pattern"
          errors={errors.namespacePattern}
          required
        >
          <Input
            id="anp-namespace-pattern"
            name="namespacePattern"
            value={values.namespacePattern}
            onChange={(e) => setFieldValue('namespacePattern', e.target.value)}
            placeholder={`e.g. user-${USER_ID_PLACEHOLDER}`}
            data-cy="anp-namespace-pattern-input"
          />
        </FormControl>

        <TextTip color="blue">
          <code>{USER_ID_PLACEHOLDER}</code> is replaced with the Portainer user
          ID.
          {examplePattern && (
            <>
              {' '}
              The user with ID {EXAMPLE_USER_ID} gets the namespace{' '}
              <code>{examplePattern}</code>.
            </>
          )}
        </TextTip>
      </FormSection>
    </>
  );

  function toggleUserScope(scope: number, checked: boolean) {
    const others = values.userScopes.filter((value) => value !== scope);
    setFieldValue('userScopes', checked ? [...others, scope] : others);
  }
}
